import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  RESPONSABILIDADE,
  baixarArquivo,
  nomeParaDownload,
  type EstadoDaLegalizacao,
  type ModeloDeContrato,
} from "../lib/legaliza";

// O editor de modelo do Legaliza Fácil: o .docx renderizado fielmente na tela,
// preenchível, e com duas saídas — imprimir em PDF, ou baixar o modelo original.
//
// ===========================================================================
// Por que o `.docx` que se baixa é o ORIGINAL, e não o editado
// ===========================================================================
// O app de origem tem um botão que baixa o documento EDITADO como `.docx`, via
// `html-docx-js-typescript`. Abri o arquivo que ele produz, em 25/08:
//
//     [Content_Types].xml  →  <Override PartName="/word/afchunk.mht"
//                                       ContentType="message/rfc822"/>
//     word/document.xml    →  <w:body><w:altChunk r:id="htmlChunk"/></w:body>
//
// É um ZIP válido com **zero OOXML de conteúdo**: o corpo inteiro é um MHTML
// embutido por `altChunk`, uma extensão que o Microsoft Word processa ao abrir e
// que Google Docs, LibreOffice e as prévias de celular não implementam. Medido
// nos dois extremos do acervo: 16 kB no modelo da casa, 2,01 MB no de Goiás (as
// oito imagens viram data: URI dentro do MHTML).
//
// O aluno é contador e manda o contrato ao cliente dele. Um arquivo que abre em
// branco na ponta é pior do que não ter o botão — e o modo de falha aparece na
// casa de outra pessoa, onde ninguém pode consertar.
//
// Então as duas saídas são as que funcionam em qualquer lugar:
//
//   · **Imprimir / Salvar PDF** — `window.print()` com `@media print`, como o
//     resumo de honorários e o currículo já fazem. O texto sai vetorial e
//     selecionável. É a mesma decisão da 000100: o Gerador de Currículos
//     abandonou html2canvas+jsPDF porque o PDF saía como imagem e o sistema de
//     recrutamento não conseguia ler. Contrato social tem o mesmo requisito, com
//     um agravante: cartório e junta trabalham com texto.
//   · **Baixar .docx** — o modelo ORIGINAL, íntegro, byte a byte do bucket. Quem
//     prefere preencher no Word abre este e edita lá, que é o fluxo real da
//     maioria.
//
// Referendado por Victor em 25/08, com a medição na mão.
//
// ===========================================================================
// O rascunho guarda TEXTO, nunca markup
// ===========================================================================
// Preencher um contrato leva tempo, e perder tudo ao recarregar tornaria o editor
// inútil — a mesma razão pela qual o checklist de admissão do HUB DP guarda as
// marcas no `localStorage`.
//
// O que se guarda é o **texto de cada elemento-folha**, indexado por um `data-i`
// que este arquivo atribui antes de liberar a edição. Não o `innerHTML`, e as
// duas razões pesam igual:
//
//   1. **Segurança.** Restaurar `innerHTML` a partir do que está guardado seria
//      criar markup a partir de dado — a superfície que o hub inteiro evita
//      (`lib/textoRico.ts` existe para isso, e não há um `dangerouslySetInnerHTML`
//      no projeto). Aplicar `textContent` num elemento que já existe não cria
//      elemento nenhum.
//   2. **Tamanho.** O `innerHTML` do modelo de Goiás tem 1,95 MB só de data: URI
//      de imagem — dois modelos desses estouram a cota do `localStorage`, e o modo
//      de falha do `setItem` é uma exceção no meio da digitação.
//      O texto do mesmo modelo tem 5,8 mil caracteres.
//
// A consequência honesta, e a tela diz: parágrafo NOVO que o aluno acrescente não
// volta depois de recarregar. O que o modelo já tem, volta.

const CIENTE = "cf-legaliza-ciente";
const RASCUNHO = "cf-legaliza-rascunho:";

type Fase = "carregando" | "pronto" | "erro";

export default function LegalizaRedator({
  estado,
  modelo,
}: {
  estado: EstadoDaLegalizacao;
  modelo: ModeloDeContrato;
}) {
  const folha = useRef<HTMLDivElement>(null);
  const [fase, setFase] = useState<Fase>("carregando");
  const [mexeu, setMexeu] = useState(false);
  const [rascunho, setRascunho] = useState<"guardado" | "restaurado" | "grande" | "nenhum">(
    "nenhum",
  );
  const [baixando, setBaixando] = useState(false);
  const [erroDownload, setErroDownload] = useState(false);
  /** Incrementa para forçar a re-renderização do modelo em branco. */
  const [versao, setVersao] = useState(0);
  const [pedirCiencia, setPedirCiencia] = useState(() => {
    try {
      return localStorage.getItem(CIENTE) !== "1";
    } catch {
      // Navegador com armazenamento bloqueado: pergunta toda vez. Melhor
      // perguntar demais que deixar de avisar.
      return true;
    }
  });

  const chave = RASCUNHO + modelo.id;

  // --- carregar e renderizar o .docx ---
  useEffect(() => {
    let vivo = true;
    setFase("carregando");
    (async () => {
      const alvo = folha.current;
      if (!alvo) return;
      const blob = await baixarArquivo(modelo.arquivo);
      if (!vivo) return;
      if (!blob) {
        setFase("erro");
        return;
      }
      try {
        const { renderAsync } = await import("docx-preview");
        alvo.innerHTML = "";
        await renderAsync(await blob.arrayBuffer(), alvo, undefined, {
          inWrapper: false,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: false,
          useBase64URL: true,
          experimental: true,
          trimXmlDeclaration: true,
          renderHeaders: true,
          renderFooters: true,
        });
        if (!vivo) return;
        const total = marcarFolhas(alvo);
        // `versao` muda quando o aluno pede o modelo em branco: aí não se
        // restaura nada, senão o rascunho que ele acabou de descartar voltaria.
        setRascunho(versao === 0 ? restaurar(alvo, chave, total) : "nenhum");
        setFase("pronto");
      } catch {
        if (vivo) setFase("erro");
      }
    })();
    return () => {
      vivo = false;
    };
    // `modelo.arquivo` é a identidade do documento; trocar de modelo re-renderiza.
  }, [modelo.arquivo, chave, versao]);

  // --- guardar o rascunho enquanto se digita ---
  useEffect(() => {
    if (fase !== "pronto" || !mexeu) return;
    const t = setTimeout(() => {
      const alvo = folha.current;
      if (!alvo) return;
      setRascunho(guardar(alvo, chave));
    }, 800);
    return () => clearTimeout(t);
  }, [fase, mexeu, chave]);

  // --- avisar antes de sair com edição na tela ---
  //
  // O rascunho cobre o recarregamento, mas não cobre fechar a aba num navegador
  // que limpa o armazenamento, nem a edição feita nos 800 ms antes do próximo
  // salvamento. Uma linha, e ela é a diferença entre perder vinte minutos e não
  // perder.
  useEffect(() => {
    if (!mexeu) return;
    const aviso = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", aviso);
    return () => window.removeEventListener("beforeunload", aviso);
  }, [mexeu]);

  async function baixarOriginal() {
    setBaixando(true);
    setErroDownload(false);
    const blob = await baixarArquivo(modelo.arquivo);
    setBaixando(false);
    if (!blob) {
      setErroDownload(true);
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = nomeParaDownload(modelo.titulo, modelo.formato, modelo.uf);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /**
   * Apaga o rascunho e traz o modelo em branco de volta.
   *
   * Ele não re-renderiza aqui: incrementa `versao`, e o efeito de cima faz o
   * trabalho. Uma segunda cópia da renderização — que foi como isto nasceu — é
   * exatamente o tipo de duplicação que diverge na primeira vez que alguém
   * ajustar as opções do `renderAsync` num lugar só.
   */
  function limparRascunho() {
    try {
      localStorage.removeItem(chave);
    } catch {
      /* nada a fazer: o rascunho já não estava sendo guardado */
    }
    setRascunho("nenhum");
    setMexeu(false);
    setVersao((v) => v + 1);
  }

  return (
    <>
      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <div className="no-imprimir">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <Link
                to={`/ferramenta/legaliza-facil/${estado.uf}`}
                className="text-sm font-semibold text-link hover:underline"
              >
                ‹ {estado.nome}
              </Link>
              <h1 className="mt-1 font-display text-xl font-bold text-titulo">{modelo.titulo}</h1>
              <p className="text-sm text-cinza">
                {modelo.origem === "junta"
                  ? `Modelo da ${estado.junta}`
                  : "Modelo da Contabilidade Facilitada"}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => window.print()}
                disabled={fase !== "pronto"}
                className="pill-cta text-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                Imprimir / Salvar PDF
              </button>
              <button
                type="button"
                onClick={baixarOriginal}
                disabled={baixando}
                className="btn-contorno text-sm disabled:opacity-50"
              >
                {baixando ? "Baixando…" : "Baixar .docx original"}
              </button>
            </div>
          </div>

          {/* A instrução vive no MOMENTO do clique, e não só num painel anterior:
              no celular não existe hover, então `title` não aparece. É a mesma
              lição do botão de PDF do Gerador de Currículos, referendada em
              25/08. */}
          <p className="mt-2 text-xs leading-relaxed text-cinza">
            Preencha os campos direto no documento abaixo — clique no texto e escreva. Para gerar
            o PDF, use <strong className="text-tinta">Imprimir</strong> e escolha{" "}
            <strong className="text-tinta">Salvar como PDF</strong> no destino. O botão{" "}
            <strong className="text-tinta">.docx</strong> entrega o modelo em branco, do jeito que
            a junta publicou, para quem prefere preencher no Word.
          </p>

          {erroDownload && (
            <p className="mt-2 text-xs font-semibold text-erro-tinta">
              Não conseguimos baixar o arquivo agora. O documento na tela continua aqui.
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
            <span data-rascunho={rascunho} className="text-cinza">
              {rascunho === "restaurado" && "Rascunho deste navegador restaurado."}
              {rascunho === "guardado" && "Rascunho guardado neste navegador."}
              {rascunho === "grande" &&
                "Este modelo é grande demais para guardar rascunho automático — imprima antes de sair."}
              {rascunho === "nenhum" && "O que você escrever fica guardado neste navegador."}
            </span>
            {(rascunho === "guardado" || rascunho === "restaurado") && (
              <button
                type="button"
                onClick={limparRascunho}
                className="font-semibold text-link hover:underline"
              >
                Recomeçar do modelo em branco
              </button>
            )}
          </div>
        </div>

        {/* A FOLHA é papel: branca nos dois temas, com a cor que o documento
            trouxe. Cor semântica aqui faria o contrato mudar de aparência
            conforme o tema de quem edita — a mesma decisão de
            `documentosMei.ts` e da folha do currículo. A moldura em volta usa
            tokens; a folha, não. */}
        <div className="relative mt-5">
          {fase === "carregando" && (
            <div className="no-imprimir absolute inset-0 z-10 grid place-items-center rounded-cf-lg bg-white/80">
              <p className="text-sm font-semibold text-[#17233d]">Carregando o modelo…</p>
            </div>
          )}
          {fase === "erro" && (
            <div className="surface p-8 text-center">
              <p className="font-display text-lg font-bold text-titulo">
                Não conseguimos abrir este modelo
              </p>
              <p className="mt-1 text-sm text-cinza">
                Você ainda pode baixar o arquivo pelo botão acima e abri-lo no Word.
              </p>
            </div>
          )}
          <div
            className={`area-de-impressao mx-auto overflow-x-auto rounded-cf-lg bg-white text-black shadow-sm ${
              fase === "erro" ? "hidden" : ""
            }`}
          >
            <div
              ref={folha}
              contentEditable={fase === "pronto"}
              suppressContentEditableWarning
              onInput={() => setMexeu(true)}
              spellCheck={false}
              data-folha="modelo"
              className="mx-auto p-8 focus:outline-none sm:p-12"
              style={{ maxWidth: "210mm", minHeight: "290mm" }}
            />
          </div>
        </div>
      </main>

      {/* O aviso de responsabilidade, uma vez por navegador. `<dialog>` nativo:
          Esc, armadilha de foco, `aria-modal` e backdrop vêm de graça — no app de
          origem o Tab levava o foco para trás do modal. */}
      <DialogoDeCiencia
        aberto={pedirCiencia}
        onAceitar={() => {
          try {
            localStorage.setItem(CIENTE, "1");
          } catch {
            /* sem armazenamento: pergunta de novo na próxima visita */
          }
          setPedirCiencia(false);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// O rascunho
// ---------------------------------------------------------------------------

/** Quantos caracteres de texto ainda cabem com folga na cota do navegador. */
const LIMITE_RASCUNHO = 400_000;

/**
 * Os elementos que TÊM texto e NÃO são texto do documento.
 *
 * O docx-preview injeta `<style>` dentro do container, e um `<style>` passa no
 * teste de folha (tem texto, não tem filho elemento). A primeira versão de
 * `marcarFolhas` o marcou como `data-i="0"`, e três coisas aconteceram, medidas
 * em 25/08: o rascunho guardava ~16 kB de CSS como se fosse texto do aluno; a
 * restauração escreveria esse CSS velho por cima do que uma versão nova da
 * biblioteca injetasse; e a prova de tela, ao escrever a marca no "primeiro
 * elemento de texto", escreveu dentro do `<style>` — onde `innerHTML` não parseia
 * e `innerText` não renderiza —, e a sabotagem da restauração por `innerHTML`
 * passou verde SEM medir nada. Um elemento que não é do documento não pode ser
 * folha do documento.
 */
const NAO_SAO_TEXTO = new Set(["STYLE", "SCRIPT", "TITLE", "META", "LINK", "TEMPLATE", "NOSCRIPT"]);

/**
 * Marca cada elemento-FOLHA de texto com um índice estável.
 *
 * Folha = elemento sem filho elemento e com texto, fora da lista acima. É onde o
 * docx-preview põe os pedaços de texto do documento, e é o nível certo para
 * guardar: marcar o `<p>` inteiro e restaurar com `textContent` apagaria o
 * negrito de dentro dele.
 */
function marcarFolhas(raiz: HTMLElement): number {
  let i = 0;
  for (const el of Array.from(raiz.querySelectorAll<HTMLElement>("*"))) {
    if (NAO_SAO_TEXTO.has(el.tagName)) continue;
    if (el.children.length === 0 && (el.textContent ?? "").trim() !== "") {
      el.dataset.i = String(i);
      i += 1;
    }
  }
  return i;
}

function guardar(raiz: HTMLElement, chave: string): "guardado" | "grande" {
  const mapa: Record<string, string> = {};
  let tamanho = 0;
  for (const el of Array.from(raiz.querySelectorAll<HTMLElement>("[data-i]"))) {
    const t = el.textContent ?? "";
    mapa[el.dataset.i as string] = t;
    tamanho += t.length;
    if (tamanho > LIMITE_RASCUNHO) return "grande";
  }
  try {
    localStorage.setItem(chave, JSON.stringify(mapa));
    return "guardado";
  } catch {
    // Cota estourada ou armazenamento bloqueado. A tela DIZ isso — silêncio aqui
    // faria o aluno acreditar num rascunho que não existe.
    return "grande";
  }
}

function restaurar(
  raiz: HTMLElement,
  chave: string,
  total: number,
): "restaurado" | "nenhum" {
  let bruto: string | null = null;
  try {
    bruto = localStorage.getItem(chave);
  } catch {
    return "nenhum";
  }
  if (!bruto) return "nenhum";

  let mapa: Record<string, string>;
  try {
    mapa = JSON.parse(bruto) as Record<string, string>;
  } catch {
    return "nenhum";
  }

  // A guarda: o rascunho tem de ter sido feito sobre ESTE documento. Sem ela, um
  // modelo trocado no bucket faria o texto do aluno cair em elementos que não são
  // dele — restauração parcial e silenciosa é pior que nenhuma.
  const chaves = Object.keys(mapa).length;
  if (chaves === 0 || chaves !== total) return "nenhum";

  let aplicados = 0;
  for (const el of Array.from(raiz.querySelectorAll<HTMLElement>("[data-i]"))) {
    const valor = mapa[el.dataset.i as string];
    // `textContent`, e nunca `innerHTML`: aqui não se cria elemento a partir de
    // dado guardado. É a mesma razão de `profiles.redes` guardar o handle e não
    // a URL.
    if (typeof valor === "string" && valor !== el.textContent) {
      el.textContent = valor;
      aplicados += 1;
    }
  }
  return aplicados > 0 ? "restaurado" : "nenhum";
}

// ---------------------------------------------------------------------------

function DialogoDeCiencia({ aberto, onAceitar }: { aberto: boolean; onAceitar: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (aberto && !d.open) d.showModal();
    if (!aberto && d.open) d.close();
  }, [aberto]);

  return (
    <dialog
      ref={ref}
      // Fechar sem marcar a ciência não vale: o `cancel` (Esc) é impedido, e a
      // única saída é o botão. É como o app de origem faz, e o aviso é da CF.
      onCancel={(e) => e.preventDefault()}
      className="m-auto w-[min(34rem,92vw)] rounded-cf-lg border border-borda bg-superficie p-0 text-tinta backdrop:bg-black/50"
    >
      <div className="p-6">
        <h2 className="font-display text-lg font-bold text-titulo">Antes de continuar</h2>
        <p className="mt-3 text-sm leading-relaxed">{RESPONSABILIDADE}</p>
        <button type="button" onClick={onAceitar} className="pill-cta mt-5 w-full justify-center">
          Li e estou ciente
        </button>
      </div>
    </dialog>
  );
}
